"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseBoard = parseBoard;
exports.parseLibraryPackages = parseLibraryPackages;
exports.readModule = readModule;
exports.readFootprint = readFootprint;
exports.readBoardLayers = readBoardLayers;
exports.readBoardLayer = readBoardLayer;
exports.readTrackFromWire = readTrackFromWire;
exports.readSegmentFromWire = readSegmentFromWire;
exports.readVia = readVia;
exports.readCircle = readCircle;
exports.readRectangle = readRectangle;
exports.readZone = readZone;
exports.readPolygon = readPolygon;
exports.readHole = readHole;
exports.readText = readText;
exports.readElementAttributeToText = readElementAttributeToText;
exports.readPad = readPad;
exports.readSmd = readSmd;
exports.readLayerSetup = readLayerSetup;
const xml_js_1 = require("xml-js");
const layers_1 = require("./layers");
const Area_1 = require("../Area");
const common_1 = require("./common");
const Util_1 = require("../Util");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const fonts_1 = require("../Fonts/fonts");
const Common_1 = require("./Types/Common");
const PAD_SHAPE_SQUARE = 'square';
const PAD_SHAPE_LONG = 'oval';
const PAD_SHAPE_OCTAGON = 'octagon';
const PAD_SHAPE_CIRCLE = 'circle';
const VIA_SHAPE_ROUND = 'round';
const LAYER_DIMENSION = 20;
const LAYER_COPPER_TOP = 1;
const LAYER_COPPER_BOTTOM = 16;
const LAYER_CREAM_TOP = 31;
const LAYER_CREAM_BOTTOM = 32;
const LAYER_SILK_TOP = 21;
const LAYER_SILK_BOTTOM = 22;
const LAYER_STOP_TOP = 29;
const LAYER_STOP_BOTTOM = 30;
const LAYER_FABRICATION_BOTTOM = 49;
const LAYER_FABRICATION_TOP = 50;
const LAYER_DOCUMENTATION = [48, 51, 52];
function isCopperLayer(layer) {
    return !!layer && layer >= 1 && layer <= 16;
}
function parseBoard(project, board, designId) {
    if (project.type !== 'eagle') {
        throw new Error('Cannot parse eagle board to non-eagle project');
    }
    const xmlRoot = parseBoardXml(board);
    const model = {
        modelType: 'pcb',
        designId,
        sourceFile: designId,
        edges_bbox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        initial_zoombox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        grid: (0, common_1.readEagleGrid)(xmlRoot.eagle.drawing.grid, 'pcb'),
        layerSet: layers_1.EAGLE_BRD_LAYERSET,
        throughHoleLayers: readLayerSetup(xmlRoot.eagle.drawing.board),
        edges: [],
        drawings: {},
        modules: [],
        tracks: {},
        vias: [],
        zones: {},
        nets: {},
        font: {
            default: fonts_1.Font.Newstroke,
        },
        metadata: {
            title: '',
            revision: '',
            company: '',
            date: '',
        },
        warnings: [],
    };
    function addDrawingToRoot(drawing, layer) {
        const layerNo = typeof layer === 'number' ? layer : layer.id;
        if (!model.drawings[layerNo]) {
            model.drawings[layerNo] = [drawing];
        }
        else {
            model.drawings[layerNo].push(drawing);
        }
    }
    if (Object.keys(project.components).length === 0) {
        model.warnings.push(`Parse schematics first for proper component linking`);
    }
    const packages = collectPackages((0, Common_1.toArray)(xmlRoot.eagle.drawing.board.libraries.library));
    readBoardLayers(project, xmlRoot, model);
    const signals = readSignals((0, Common_1.toArray)(xmlRoot.eagle.drawing.board.signals.signal));
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.wire)?.forEach((w) => {
        const track = readTrackFromWire(w, '');
        if (track.layer === LAYER_DIMENSION) {
            const segment = readSegmentFromWire(w);
            model.edges.push(segment);
        }
        else if (isCopperLayer(track.layer)) {
            model.tracks[track.layer] = model.tracks[track.layer] ?? [];
            model.tracks[track.layer].push(track);
        }
        else {
            const segment = readSegmentFromWire(w);
            addDrawingToRoot(segment, track.layer);
        }
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.rectangle)?.forEach((r) => {
        const rectangle = readRectangle(r);
        addDrawingToRoot(rectangle.shape, rectangle.layer);
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.circle)?.forEach((c) => {
        const { shape, layer } = readCircle(c);
        addDrawingToRoot(shape, layer);
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.hole)?.forEach((h) => {
        model.edges.push(readHole(h));
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.text)?.forEach((t) => {
        const { shape, layer } = readText(t);
        addDrawingToRoot(shape, layer);
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.signals.signal)?.forEach((s) => {
        (0, Common_1.toArray)(s.wire).forEach((w) => {
            const track = readTrackFromWire(w, s._attributes.name);
            if (typeof track.layer === 'number') {
                model.tracks[track.layer] = model.tracks[track.layer] ?? [];
                model.tracks[track.layer].push(track);
            }
            else {
                model.warnings.push(`Layer ${track.layer} not implemented.`);
            }
        });
        (0, Common_1.toArray)(s.via).forEach((v) => {
            const via = readVia(v, s._attributes.name, model.throughHoleLayers);
            model.vias.push(via);
        });
        (0, Common_1.toArray)(s.polygon).forEach((p) => {
            const zone = readZone(p, s._attributes.name);
            if (typeof zone.layer === 'number') {
                model.zones[zone.layer] = model.zones[zone.layer] ?? [];
                model.zones[zone.layer].push(zone);
            }
            else {
                model.warnings.push(`Layer ${zone.layer} not implemented.`);
            }
        });
    });
    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.elements.element).forEach((e) => {
        let eaglePackage = selectPackage(packages, e._attributes.library, e._attributes.package);
        const { footprint, module, value } = readModule(project, e, eaglePackage, signals);
        const component = project.components[module.componentRef];
        if (!component) {
            project.components[footprint.ref] = {
                name: footprint.ref,
                footprints: [footprint],
                signals: [],
                symbols: [],
                numPins: footprint.pads.length,
                numPads: footprint.pads.length,
            };
        }
        model.modules.push(module);
        for (const [pin, net] of Object.entries(module.pinNets)) {
            if (!model.nets[net]) {
                model.nets[net] = {
                    name: net,
                    connections: [],
                    isGlobal: false,
                };
                if (net.toLowerCase().endsWith('_p') || net.toLowerCase().endsWith('_n')) {
                    const pairName = net.slice(0, -2);
                    model.nets[net].diffPair = {
                        pairName,
                        positiveId: net.toLowerCase().endsWith('_p') ? net : pairName + '_P',
                        negativeId: net.toLowerCase().endsWith('_n') ? net : pairName + '_N',
                    };
                }
            }
            model.nets[net].connections.push({
                moduleRef: module.ref,
                pinRef: pin,
                moduleName: module.name,
            });
        }
        let metaModule = project.modules.find((m) => m.name === module.name);
        if (!metaModule) {
            metaModule = {
                name: module.fullModuleName ?? module.name,
                instances: [],
                componentRef: module.componentRef,
            };
            project.modules.push(metaModule);
        }
        if (!metaModule.name) {
            metaModule.name = module.name;
        }
        if (value) {
            if (metaModule.value && metaModule.value !== value) {
                model.warnings.push(`Module ${module.name} has multiple values: ${metaModule.value} and ${value}`);
            }
            metaModule.value = value;
        }
        if (module.metadata && Object.keys(module.metadata).length > 0) {
            if (!metaModule.metadata) {
                metaModule.metadata = {};
            }
            metaModule.metadata = {
                ...metaModule.metadata,
                ...module.metadata,
            };
        }
        metaModule.instances.push({
            ref: module.ref,
            designId,
        });
    });
    for (const net of Object.values(model.nets)) {
        if (!net.diffPair) {
            continue;
        }
        if (!model.nets[net.diffPair.positiveId]) {
            model.warnings.push(`Could not find positive net for differential pair '${net.diffPair.positiveId}'`);
        }
        if (!model.nets[net.diffPair.positiveId]) {
            model.warnings.push(`Could not find negative net for differential pair '${net.diffPair.positiveId}'`);
        }
    }
    const bbox = new Area_1.default();
    bbox.extendFromPcbModel(project, model, project.layerSets[layers_1.EAGLE_BRD_LAYERSET], model.warnings);
    model.edges_bbox = {
        minx: bbox.xMin,
        miny: bbox.yMin,
        maxx: bbox.xMax,
        maxy: bbox.yMax,
    };
    const visibleBbox = new Area_1.default();
    visibleBbox.extendFromPcbModel(project, model, project.layerSets[layers_1.EAGLE_BRD_LAYERSET], model.warnings, false);
    model.initial_zoombox = {
        minx: visibleBbox.xMin,
        miny: visibleBbox.yMin,
        maxx: visibleBbox.xMax,
        maxy: visibleBbox.yMax,
    };
    (0, Util_1.dedupeWarnings)(model);
    return model;
}
function parseLibraryPackages(library, filename) {
    const xmlRoot = parseLibraryXml(library);
    return collectPackages((0, Common_1.toArray)(xmlRoot.eagle.drawing.library), filename);
}
function parseBoardXml(board) {
    const json = (0, xml_js_1.xml2json)(board, { compact: true });
    return JSON.parse(json);
}
function parseLibraryXml(library) {
    const json = (0, xml_js_1.xml2json)(library, { compact: true });
    return JSON.parse(json);
}
function collectPackages(libs, defaultLibName = '<unknownlib>') {
    const packages = {};
    libs.forEach((l) => {
        (0, Common_1.toArray)(l.packages.package).forEach((p) => {
            packages[`${l._attributes?.name || defaultLibName}::${p._attributes.name}`] = p;
        });
    });
    return packages;
}
function selectPackage(packages, libraryName, packageName) {
    return packages[`${libraryName}::${packageName}`];
}
function readModule(project, element, eaglePackage, signals) {
    const rotation = (0, common_1.parseRotation)(element._attributes.rot);
    const module = {
        ref: element._attributes.name,
        footprintRef: '',
        componentRef: '',
        name: element._attributes.name,
        center: [parseFloat(element._attributes.x), -parseFloat(element._attributes.y)],
        transform: {
            pos: [0, 0],
            angle: -1 * rotation.rot,
            mirrored: rotation.mirrored,
        },
        pinNets: {},
        drawProperties: {},
        layer: element._attributes.rot?.startsWith('MR') ? 'B' : 'F',
    };
    const footprint = readFootprint(eaglePackage, element._attributes.name, element._attributes.library, module, signals);
    module.footprintRef = footprint.ref;
    module.componentRef = getComponentRefForElement(project, element._attributes.name, footprint.ref);
    const value = element._attributes.value?.trim();
    (0, Common_1.toArray)(element.attribute).forEach((r) => {
        readElementAttributeToText(r, module, value);
    });
    return { footprint, module, value };
}
function readFootprint(eaglePackage, deviceName, libraryName, module, signals = []) {
    const footprint = {
        ref: `${libraryName}::footprint::${eaglePackage._attributes.name}`,
        pads: [],
        drawings: [],
        name: `${libraryName}::footprint::${eaglePackage._attributes.name}`,
        metadata: {
            'eagle.library': libraryName,
        },
    };
    (0, common_1.addMetadata)(footprint, 'eagle.package.name', eaglePackage._attributes?.name);
    (0, Common_1.toArray)(eaglePackage.pad).forEach((p) => {
        const { pad, net } = readPad(p, deviceName, signals);
        if (module) {
            module.pinNets[pad.ref] = net;
        }
        footprint.pads.push(pad);
    });
    (0, Common_1.toArray)(eaglePackage.smd).forEach((smd) => {
        const { pad, net } = readSmd(smd, deviceName, signals);
        if (module) {
            module.pinNets[pad.ref] = net;
        }
        footprint.pads.push(pad);
    });
    (0, Common_1.toArray)(eaglePackage.wire).forEach((w) => {
        const track = readTrackFromWire(w, '');
        if (track.layer === LAYER_SILK_BOTTOM || track.layer === LAYER_SILK_TOP) {
            const segment = readSegmentFromWire(w);
            footprint.drawings.push({ layers: [track.layer], drawing: segment });
        }
        else {
            footprint.drawings.push({ layers: [track.layer], drawing: track });
        }
    });
    (0, Common_1.toArray)(eaglePackage.polygon).forEach((p) => {
        const poly = readPolygon(p);
        const polyLayer = parseInt(p._attributes.layer);
        if (typeof polyLayer === 'number') {
            footprint.drawings.push({ layers: [polyLayer], drawing: poly });
        }
    });
    (0, Common_1.toArray)(eaglePackage.rectangle).forEach((r) => {
        const { shape, layer } = readRectangle(r);
        footprint.drawings.push({ layers: [layer], drawing: shape });
    });
    (0, Common_1.toArray)(eaglePackage.circle).forEach((c) => {
        const { shape, layer } = readCircle(c);
        footprint.drawings.push({ layers: [layer], drawing: shape });
    });
    (0, Common_1.toArray)(eaglePackage.hole).forEach((c) => {
        const hole = readHole(c);
        footprint.drawings.push({ layers: [layers_1.LAYER_EDGE], drawing: hole });
    });
    if (eaglePackage.description) {
        footprint.metadata['description'] = (0, Common_1.toArray)(eaglePackage.description)
            .map((d) => d._text)
            .join('\n');
    }
    (0, Common_1.toArray)(eaglePackage.text).forEach((r) => {
        const { shape, layer } = readText(r);
        footprint.drawings.push({ layers: [layer], drawing: shape });
    });
    return footprint;
}
function readBoardLayers(project, xmlRoot, model) {
    if (project.layerSets[layers_1.EAGLE_BRD_LAYERSET]) {
        return;
    }
    const layers = [];
    xmlRoot.eagle.drawing.layers.layer.forEach((l) => {
        if (!(0, layers_1.isLayerActive)(l)) {
            return;
        }
        layers.push(readBoardLayer(l));
    });
    for (const layer of layers) {
        if (!layer.name) {
            model.warnings.push(`Layer ${layer.id} did not have a name associated`);
            continue;
        }
        const match = /^(_?)(t|b)([A-Z]\S+)$/.exec(layer.name);
        if (typeof layer.face === 'undefined' && match) {
            if (match[2] === 't') {
                layer.face = 'F';
            }
            else if (match[2] === 'b') {
                layer.face = 'B';
            }
        }
        if (typeof layer.face !== 'undefined' && typeof layer.faceMirrorLayer === 'undefined' && match) {
            const oppositeLayerName = [match[1] || '', layer.face === 'F' ? 'b' : 't', match[3]].join('');
            const opposite = layers.find((l) => l.name === oppositeLayerName);
            if (opposite) {
                if (typeof opposite.faceMirrorLayer !== 'undefined') {
                    if (opposite.faceMirrorLayer !== layer.id) {
                        model.warnings.push(`Layer ${layer.id} should have opposite ${opposite.id}, but opposite has faceMirrorLayer ${opposite.faceMirrorLayer}`);
                        continue;
                    }
                }
                layer.faceMirrorLayer = opposite.id;
                opposite.faceMirrorLayer = layer.id;
            }
        }
    }
    project.layerSets[layers_1.EAGLE_BRD_LAYERSET] = layers;
}
function readBoardLayer(layer) {
    const layerId = parseInt(layer._attributes.number);
    const parsed = (0, layers_1.parsePcbLayer)(layerId, layer._attributes.name);
    if ((0, layers_1.isLayerVisible)(layer)) {
        parsed.defaultVisible = true;
    }
    return parsed;
}
function readTrackFromWire(wire, net) {
    return {
        type: 'track',
        start: [parseFloat(wire._attributes.x1), -parseFloat(wire._attributes.y1)],
        end: [parseFloat(wire._attributes.x2), -parseFloat(wire._attributes.y2)],
        width: parseFloat(wire._attributes.width || '0'),
        net,
        layer: parseInt(wire._attributes.layer),
    };
}
function readSegmentFromWire(wire) {
    const width = parseFloat(wire._attributes.width ?? '0');
    return {
        type: 'segment',
        start: [parseFloat(wire._attributes.x1), -parseFloat(wire._attributes.y1)],
        end: [parseFloat(wire._attributes.x2), -parseFloat(wire._attributes.y2)],
        width: width >= 0 ? width : 0,
        curve: parseFloat(wire._attributes.curve ?? '0'),
    };
}
function readVia(via, net, viaLayers) {
    const diameter = parseFloat(via._attributes.drill) * 2;
    let polygons = [];
    let shape;
    switch (via._attributes.shape) {
        case PAD_SHAPE_SQUARE:
            shape = 'rect';
            break;
        case PAD_SHAPE_OCTAGON: {
            shape = 'custom';
            polygons = getOctagonPoints(diameter / 2);
            break;
        }
        case VIA_SHAPE_ROUND:
        default:
            shape = 'circle';
    }
    return {
        type: 'via',
        center: [parseFloat(via._attributes.x), -parseFloat(via._attributes.y)],
        diameter: parseFloat(via._attributes.drill) * 2,
        drillDiameter: parseFloat(via._attributes.drill),
        layers: [...viaLayers, 18],
        net,
        shape,
        polygons: polygons.length ? polygons : undefined,
    };
}
function readCircle(c) {
    return {
        layer: parseFloat(c._attributes.layer),
        shape: {
            type: 'circle',
            center: [parseFloat(c._attributes.x), -parseFloat(c._attributes.y)],
            radius: parseFloat(c._attributes.radius),
            filled: false,
        },
    };
}
function readRectangle(r, net) {
    return {
        layer: parseFloat(r._attributes.layer),
        shape: {
            type: 'rect',
            start: [parseFloat(r._attributes.x1), -parseFloat(r._attributes.y1)],
            end: [parseFloat(r._attributes.x2), -parseFloat(r._attributes.y2)],
            filled: true,
            strokeWidth: r._attributes.width ? parseFloat(r._attributes.width) : 0,
        },
    };
}
function readZone(polygon, net) {
    const points = polygon.vertex.map((v) => [parseFloat(v._attributes.x), -parseFloat(v._attributes.y)]);
    points.push(points[0]);
    return {
        type: 'zone',
        layer: parseFloat(polygon._attributes.layer),
        polygons: [
            {
                points,
                filled: false,
            },
        ],
        net,
    };
}
function readPolygon(polygon, metadata) {
    const points = polygon.vertex.map((v) => [parseFloat(v._attributes.x), -parseFloat(v._attributes.y)]);
    points.push(points[0]);
    return {
        type: 'polygon',
        points,
        width: parseFloat(polygon._attributes.width),
        filled: true,
        metadata,
    };
}
function readHole(hole) {
    return {
        type: 'circle',
        center: [parseFloat(hole._attributes.x), -parseFloat(hole._attributes.y)],
        radius: parseFloat(hole._attributes.drill) / 2,
        filled: true,
    };
}
function readText(text, isPositionOffset, rejustify) {
    let ref_designator = false;
    let ref = undefined;
    if (text._text?.startsWith('>')) {
        ref_designator = true;
        ref = text._text.substring(1);
    }
    let rot = (0, common_1.parseRotation)(text._attributes.rot);
    let justify = [-1, -1];
    rot.rot *= -1;
    if (rejustify) {
        let result = (0, Transform_1.rejustifyTextUpwards)(justify, rot);
        rot = result.rot;
        justify = result.justify;
    }
    return {
        layer: parseFloat(text._attributes.layer),
        shape: {
            type: 'text',
            size: parseFloat(text._attributes.size),
            thickness: 1,
            pos: [parseFloat(text._attributes.x), -parseFloat(text._attributes.y)],
            justify,
            angle: rot.rot,
            mirror: rot.mirrored,
            attr: [],
            ref,
            ref_designator,
            val: false,
            value: text._text,
            isPositionOffset,
            rejustifyOnRotate: rejustify,
        },
    };
}
function readElementAttributeToText(text, module, value) {
    const rotation = (0, common_1.parseRotation)(text._attributes.rot);
    rotation.rot *= -1;
    const { justify, rot } = (0, Transform_1.rejustifyTextUpwards)([-1, -1], rotation);
    const nameLower = text._attributes.name.toLowerCase();
    if (text._attributes.value) {
        value = text._attributes.value;
    }
    else if (nameLower === 'name') {
        value = module.name;
    }
    module.drawProperties[text._attributes.name] = {
        angle: rot.rot,
        mirror: rot.mirrored,
        justify,
        pos: [parseFloat(text._attributes.x), -parseFloat(text._attributes.y)],
        value: value,
        size: parseFloat(text._attributes.size),
        thickness: 1,
        layer: text._attributes.layer ? parseInt(text._attributes.layer) : undefined,
        hidden: typeof text._attributes.display === 'string' && !(0, common_1.eagleBoolean)(text._attributes.display),
        rejustifyOnRotate: true,
    };
}
function readPad(pad, footprintName, signals) {
    const name = pad._attributes.name;
    let diameter = pad._attributes.diameter ? parseFloat(pad._attributes.diameter) : 0;
    const drillDiameter = pad._attributes.drill ? parseFloat(pad._attributes.drill) : 0;
    const signal = signals.find((s) => s.contactRefs.find((c) => c.element === footprintName && c.pad === name));
    if (diameter <= 0 && drillDiameter > 0) {
        switch (pad._attributes.shape) {
            case 'octagon':
                diameter = 1.52;
                break;
            default:
                diameter = drillDiameter;
        }
    }
    const pos = [parseFloat(pad._attributes.x), -parseFloat(pad._attributes.y)];
    let polygons = [];
    let shape;
    switch (pad._attributes.shape) {
        case PAD_SHAPE_SQUARE:
            shape = 'rect';
            break;
        case PAD_SHAPE_LONG:
            shape = 'oval';
            break;
        case PAD_SHAPE_OCTAGON: {
            shape = 'custom';
            polygons = getOctagonPoints(diameter / 2);
            break;
        }
        default:
            shape = 'circle';
    }
    const result = {
        type: 'pad',
        ref: pad._attributes.name,
        name: pad._attributes.name,
        pos,
        polygons: polygons.length ? polygons : undefined,
        angle: -(0, common_1.parseRotation)(pad._attributes.rot).rot,
        pin1: name === '1' ? 1 : undefined,
        shape: shape,
        padType: 'th',
        diameter: [diameter, diameter],
    };
    if (drillDiameter > 0) {
        result.drillshape = shape === 'oval' ? 'oblong' : 'circle';
        result.drillDiameter = [drillDiameter, drillDiameter];
    }
    return { pad: result, net: signal?.name ?? '' };
}
function readSmd(smd, footprintName, signals) {
    const name = smd._attributes.name;
    const signal = signals.find((s) => s.contactRefs.find((c) => c.element === footprintName && c.pad === name));
    const pos = [parseFloat(smd._attributes.x), -parseFloat(smd._attributes.y)];
    const shape = 'roundrect';
    const roundness = smd._attributes.roundness ? parseFloat(smd._attributes.roundness) : 0;
    const diameter = [parseFloat(smd._attributes.dx), parseFloat(smd._attributes.dy)];
    const smdLayer = parseFloat(smd._attributes.layer);
    const result = {
        type: 'pad',
        ref: smd._attributes.name,
        name: smd._attributes.name,
        layers: [smdLayer],
        pos,
        angle: -(0, common_1.parseRotation)(smd._attributes.rot).rot,
        pin1: name === '1',
        shape: shape,
        padType: 'smd',
        diameter,
        radius: (roundness / 100) * Math.max(...diameter),
    };
    const drawings = [];
    if ((0, common_1.eagleBoolean)(smd._attributes.stop, true)) {
        result.layers.push(smdLayer === LAYER_COPPER_TOP ? LAYER_STOP_TOP : LAYER_STOP_BOTTOM);
    }
    if ((0, common_1.eagleBoolean)(smd._attributes.cream, true)) {
        result.layers.push(smdLayer === LAYER_COPPER_TOP ? LAYER_CREAM_TOP : LAYER_CREAM_BOTTOM);
    }
    return {
        pad: result,
        net: signal?.name ?? '',
    };
}
function readLayerSetup(board) {
    const nonCopperThroughHoleLayers = [LAYER_STOP_TOP, LAYER_STOP_BOTTOM, layers_1.LAYER_EDGE];
    const descriptionLayerSetup = board.designrules.param.find((p) => p._attributes.name === 'layerSetup');
    if (descriptionLayerSetup) {
        const values = descriptionLayerSetup._attributes.value.split(/[^0-9.]/g);
        return values
            .filter((v) => v.length)
            .map((v) => parseInt(v))
            .concat(nonCopperThroughHoleLayers)
            .sort((a, b) => a - b);
    }
    return [layers_1.LAYER_FRONT, layers_1.LAYER_BACK, ...nonCopperThroughHoleLayers];
}
function readSignals(signals) {
    return signals.map((s) => {
        return {
            name: s._attributes.name,
            class: s._attributes.class,
            contactRefs: (0, Common_1.toArray)(s.contactref).map((c) => c._attributes),
        };
    });
}
function getOctagonPoints(radius) {
    radius = radius * 1.082883029;
    const startAngle = Math.PI / 8;
    const calcX = (xi, offset) => xi + radius * Math.cos(startAngle + (offset * Math.PI) / 4);
    const calcY = (xi, offset) => xi + radius * Math.sin(startAngle + (offset * Math.PI) / 4);
    return [
        [
            [calcX(0, 0), -calcY(0, 0)],
            [calcX(0, 1), -calcY(0, 1)],
            [calcX(0, 2), -calcY(0, 2)],
            [calcX(0, 3), -calcY(0, 3)],
            [calcX(0, 4), -calcY(0, 4)],
            [calcX(0, 5), -calcY(0, 5)],
            [calcX(0, 6), -calcY(0, 6)],
            [calcX(0, 7), -calcY(0, 7)],
            [calcX(0, 0), -calcY(0, 0)],
        ],
    ];
}
function getComponentRefForElement(project, name, defaultRef) {
    for (const [, design] of Object.entries(project.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const module of design.modules) {
            if (module.name === name) {
                return module.componentRef;
            }
        }
    }
    return defaultRef;
}
//# sourceMappingURL=board.js.map